import { useCallback, useEffect, useState } from 'react';
import { approveDraft, getDrafts, rejectDraft } from './api';

const KIND_LABEL = { mini_guides: 'Bilgi yazısı', recipes: 'Tarif' };

const Warning = ({ children }) => (
  <p className="mt-2 px-3 py-2 rounded-lg bg-amber-500/15 text-amber-500 text-xs font-semibold">{children}</p>
);

const Section = ({ title, children }) => (
  <div className="mt-3">
    <p className="text-dcp-text-2 text-[11px] font-semibold uppercase tracking-wide">{title}</p>
    <div className="mt-1 text-dcp-text text-sm">{children}</div>
  </div>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-5 space-y-0.5">
    {items.map((x) => <li key={x}>{x}</li>)}
  </ul>
);

/** AI ne yazdiysa onaylayan onu gorsun: kisaltma yok, icerik tam acik. */
function DraftBody({ item }) {
  const broken = new Set(item.brokenSources ?? []);
  if (item.category === 'recipes') {
    return (
      <>
        <Warning>Kalori ve makrolar AI tahminidir, kontrol et.</Warning>
        <Section title="Değerler">
          {item.calories} kcal · P {item.macros?.protein} g · K {item.macros?.carb} g · Y {item.macros?.fat} g · {item.prepTime}
        </Section>
        <Section title="Malzemeler"><Bullets items={item.ingredients ?? []} /></Section>
        <Section title="Adımlar"><Bullets items={item.steps ?? []} /></Section>
      </>
    );
  }
  return (
    <>
      <Section title="İçerik"><p className="whitespace-pre-wrap">{item.content}</p></Section>
      <Section title="Kaynaklar (kontrol et)">
        <ul className="space-y-1">
          {(item.sources ?? []).map((url) => (
            <li key={url} className="break-all">
              <a href={url} target="_blank" rel="noreferrer noopener" className="text-dcp-primary underline">{url}</a>
              {broken.has(url) && <span className="ml-2 text-amber-500 text-xs font-semibold">açılmadı</span>}
            </li>
          ))}
        </ul>
      </Section>
    </>
  );
}

function DraftCard({ item, onEdit, onApprove, onReject }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [note, setNote] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const isWaitingRegeneration = item.status === 'rejected';

  const run = async (action) => {
    setIsBusy(true);
    await action();
    setIsBusy(false);
  };

  return (
    <article className="bg-dcp-surface border border-dcp-border rounded-2xl p-4">
      <div className="flex gap-3">
        {item.thumbnailUrl ? (
          <img src={item.thumbnailUrl} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0" />
        ) : (
          <div className="w-20 h-20 rounded-xl bg-dcp-surface-variant shrink-0" />
        )}
        <div className="min-w-0">
          <p className="text-dcp-text-2 text-[11px] font-semibold">
            {KIND_LABEL[item.category] ?? item.category} · {item.draftBatch} · {item.isPremium ? 'Premium' : 'Ücretsiz'}
          </p>
          <h2 className="text-dcp-text font-bold mt-0.5">{item.title}</h2>
          <p className="text-dcp-text-2 text-sm mt-0.5">{item.description}</p>
        </div>
      </div>

      {!item.thumbnailUrl && <Warning>Görsel yok. Yayınlamak için Düzenle'den görsel ekle.</Warning>}

      {isWaitingRegeneration ? (
        <Warning>Yeniden üretiliyor (en geç 1 saat). Not: {item.rejectNote}</Warning>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setIsOpen((v) => !v)}
            className="mt-3 text-dcp-primary text-sm font-semibold"
          >
            {isOpen ? 'İçeriği gizle' : 'İçeriği oku'}
          </button>
          {isOpen && <DraftBody item={item} />}

          {isRejecting ? (
            <div className="mt-3">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Ne değişsin? (ör. daha kısa olsun). Boş bırakırsan taslak atılır."
                className="w-full bg-dcp-bg border border-dcp-border rounded-xl px-3 py-2 text-sm text-dcp-text"
              />
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => run(() => onReject(item, note.trim()))}
                  className="h-10 px-4 rounded-xl bg-dcp-error text-white text-sm font-bold disabled:opacity-50"
                >
                  {note.trim() ? 'Notla yeniden üret' : 'Taslağı at'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsRejecting(false)}
                  className="h-10 px-4 rounded-xl text-dcp-text-2 text-sm"
                >
                  Vazgeç
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 mt-4">
              <button
                type="button"
                disabled={isBusy || !item.thumbnailUrl}
                onClick={() => run(() => onApprove(item))}
                className="h-10 px-4 rounded-xl bg-dcp-primary text-dcp-on-primary text-sm font-bold disabled:opacity-50"
              >
                Onayla ve yayınla
              </button>
              <button
                type="button"
                onClick={() => onEdit(item)}
                className="h-10 px-4 rounded-xl bg-dcp-surface-variant text-dcp-text text-sm font-semibold"
              >
                Düzenle
              </button>
              <button
                type="button"
                onClick={() => setIsRejecting(true)}
                className="h-10 px-4 rounded-xl text-dcp-error text-sm font-semibold"
              >
                Reddet
              </button>
            </div>
          )}
        </>
      )}
    </article>
  );
}

export default function DraftList({ onEdit, onCountChange }) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const drafts = await getDrafts();
      setItems(drafts);
      onCountChange?.(drafts.length);
    } catch (err) {
      setError(err.message);
    }
    setIsLoading(false);
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const guarded = async (action) => {
    setError(null);
    try {
      await action();
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <main className="max-w-3xl mx-auto px-4 py-4 space-y-3">
      <p className="text-dcp-text-2 text-sm">
        AI her hafta 2 yazı + 3 tarif hazırlar. Sen onaylamadan hiçbiri yayına çıkmaz; onaylanan hemen Keşfet'e girer ve 7 dile çevrilir.
      </p>
      {error && <p className="text-dcp-error text-sm">{error}</p>}
      {isLoading && <p className="text-dcp-text-2 text-sm">Yükleniyor…</p>}
      {!isLoading && !error && items.length === 0 && (
        <p className="text-dcp-text-2 text-sm">Onay bekleyen taslak yok.</p>
      )}
      {items.map((item) => (
        <DraftCard
          key={item._id}
          item={item}
          onEdit={onEdit}
          onApprove={(it) => guarded(() => approveDraft(it._id))}
          onReject={(it, note) => guarded(() => rejectDraft(it._id, note))}
        />
      ))}
    </main>
  );
}
